import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppLayout } from '../components/Layout';
import { ScoreRing } from '../components/ScoreRing';
import {
  Users, TrendingUp, AlertTriangle, CheckCircle,
  ArrowRight, Building, DollarSign, Clock, Target,
  BarChart3, ChevronRight, ChevronDown, Lightbulb, GraduationCap,
  ShieldCheck, ExternalLink, FileText, Lock, X, Briefcase,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('futurepath_token')}` } });
const pct = (n) => `${n ?? 0}%`;
const fmt = (n) => (n ?? 0).toLocaleString();

// Future Readiness Score: direction(40) + assessment(30) + income(20) + colleges(10)
function calcReadiness(eng) {
  if (!eng) return 0;
  return Math.round(
    (eng.direction_established_pct || 0) * 0.40 +
    (eng.assessment_complete_pct || 0) * 0.30 +
    (eng.income_goal_set_pct || 0) * 0.20 +
    (eng.college_planning_started_pct || 0) * 0.10
  );
}

export default function SuperintendentDashboardPage() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/institution/district/dashboard`, auth())
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Could not load district data'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <AppLayout>
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-400 border-t-transparent" />
      </div>
    </AppLayout>
  );

  if (error) return (
    <AppLayout>
      <div className="rounded-xl border border-rose-400/25 bg-rose-400/[0.06] p-6 text-rose-300 text-sm">
        {error}
      </div>
    </AppLayout>
  );

  const schools = data?.school_comparison || [];
  const summary = data?.summary || {};
  const district = data?.district_name || 'District';
  const allCareers = data?.career_interests || [];
  const careers  = allCareers.slice(0, 6);
  const insights = data?.insights || [];

  // Use first school engagement for readiness score
  const firstSchool = schools[0];
  const schoolEng = firstSchool?.engagement;
  const readiness = firstSchool?.future_readiness_score
    ?? (schoolEng ? calcReadiness(schoolEng) : calcReadiness({
        direction_established_pct: summary.direction_pct,
        assessment_complete_pct: summary.assessment_pct,
    }));

  const needsAttention = summary.undecided_count || 0;
  const totalStudents  = summary.total_students || 0;
  const directionPct   = summary.direction_pct || 0;
  const assessmentPct  = summary.assessment_pct || 0;

  // ROI numbers
  const undecided  = needsAttention;
  const hoursSaved = Math.round(totalStudents * 3 * 12 / 60); // 12 min × 3 meetings / 60
  const dollarValue = Math.round(hoursSaved * (75000 / 2080));

  return (
    <AppLayout>
      <div className="space-y-8" data-testid="superintendent-dashboard">

        {/* ── Arrival Hero ─────────────────────────────────────────────── */}
        <div className="relative overflow-hidden rounded-2xl border border-cyan-300/20 bg-[#0a1729] px-8 py-10">
          <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.18), transparent 65%)' }} />
          <div aria-hidden className="pointer-events-none absolute -left-10 bottom-0 h-48 w-48 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(52,211,153,0.10), transparent 70%)' }} />

          <span className="inline-flex items-center gap-2 rounded-full border border-cyan-300/30
            bg-cyan-400/[0.07] px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-cyan-200">
            <Building className="h-3 w-3" /> District Intelligence
          </span>

          <div className="mt-4 flex flex-wrap items-end gap-8">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-white sm:text-4xl">
                {district}
              </h1>
              <p className="mt-2 text-base text-slate-300 max-w-xl">
                Future Readiness Intelligence, a live view of where every student stands,
                what they need, and what it means for the district.
              </p>
            </div>
            <ScoreRing
              value={readiness}
              display={readiness}
              label="Readiness"
              palette="cyan"
              size={155}
              testId="readiness-score-ring"
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-6">
            <ScoreLabel label="Below 40" desc="Foundational stage" color="#fb923c" active={readiness < 40} />
            <ScoreLabel label="40-65" desc="Building momentum" color="#fbbf24" active={readiness >= 40 && readiness < 65} />
            <ScoreLabel label="65-85" desc="Strong trajectory" color="#34d399" active={readiness >= 65 && readiness < 85} />
            <ScoreLabel label="85+" desc="District-leading" color="#22d3ee" active={readiness >= 85} />
          </div>
        </div>

        {/* ── Key Metrics ──────────────────────────────────────────────────── */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="district-metrics">
          <MetricTile
            icon={Users}
            value={fmt(totalStudents)}
            label="Students enrolled"
            color="#22d3ee"
            testId="metric-total-students"
          />
          <MetricTile
            icon={Target}
            value={pct(directionPct)}
            label="Have a declared direction"
            color="#34d399"
            testId="metric-direction-pct"
          />
          <MetricTile
            icon={CheckCircle}
            value={pct(assessmentPct)}
            label="Assessment complete"
            color="#a78bfa"
            testId="metric-assessment-pct"
          />
          <MetricTile
            icon={AlertTriangle}
            value={fmt(needsAttention)}
            label={needsAttention === 1 ? 'Student needs attention' : 'Students need attention'}
            color="#f87171"
            urgent={needsAttention > 0}
            testId="metric-needs-attention"
          />
        </div>

        {/* ── District Health Summary ──────────────────────────────────── */}
        <div className="rounded-2xl border p-6 space-y-5"
          style={{ borderColor: 'rgba(34,211,238,0.20)', background: 'linear-gradient(135deg, rgba(6,13,26,0.95) 0%, rgba(13,28,55,0.75) 100%)' }}
          data-testid="district-health-summary">

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-cyan-400" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-300">District Health</p>
              </div>
              <h2 className="text-lg font-bold text-white">Career Readiness by School Tier</h2>
              <p className="text-[12px] text-slate-300 mt-0.5">Live breakdown of where {fmt(totalStudents)} students stand across {schools.length} schools</p>
            </div>
            <button onClick={() => navigate('/app/pathways/counselor')}
              className="flex items-center gap-2 rounded-lg border border-cyan-300/25 bg-cyan-400/[0.07]
                px-4 py-2 text-sm font-medium text-cyan-300 hover:bg-cyan-400/[0.14] transition-colors shrink-0"
              data-testid="open-command-center-btn">
              <Users className="h-4 w-4" /> Counselor Command Center
            </button>
          </div>

          {/* Tier breakdown */}
          {(() => {
            const tiers = [
              { label: 'District-Leading', min: 85, color: '#22d3ee',  bg: 'rgba(34,211,238,0.08)',  border: 'rgba(34,211,238,0.20)' },
              { label: 'Strong Trajectory', min: 65, color: '#34d399', bg: 'rgba(52,211,153,0.08)',  border: 'rgba(52,211,153,0.20)' },
              { label: 'Building Momentum', min: 40, color: '#fbbf24', bg: 'rgba(251,191,36,0.08)',  border: 'rgba(251,191,36,0.20)' },
              { label: 'Needs Support',    min: 0,  color: '#fb923c',  bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.20)' },
            ];
            return (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {tiers.map(tier => {
                  const count = schools.filter(s => {
                    const rs = s.future_readiness_score || 0;
                    const next = tiers[tiers.indexOf(tier) - 1];
                    return rs >= tier.min && (!next || rs < next.min);
                  }).length;
                  return (
                    <div key={tier.label} className="rounded-xl border p-4"
                      style={{ borderColor: tier.border, background: tier.bg }}>
                      <p className="text-2xl font-bold" style={{ color: tier.color }}>{count}</p>
                      <p className="text-[11.5px] font-medium text-slate-300 mt-0.5">{tier.label}</p>
                      <p className="text-[10px] text-slate-300 mt-0.5">schools</p>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Top 3 urgent schools */}
          {(() => {
            const urgent = [...schools]
              .filter(s => (s.undecided_count || 0) > 0)
              .sort((a, b) => (b.undecided_count || 0) - (a.undecided_count || 0))
              .slice(0, 3);
            return urgent.length > 0 ? (
              <div className="border-t border-white/[0.06] pt-4">
                <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-300 mb-3">Highest Undecided Count</p>
                <div className="space-y-2">
                  {urgent.map(s => (
                    <div key={s.id} className="flex items-center justify-between gap-4 rounded-lg bg-white/[0.025] px-4 py-2.5">
                      <p className="text-[12px] font-medium text-white truncate">{s.name}</p>
                      <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20 shrink-0">
                        {s.undecided_count} undecided
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;
          })()}
        </div>

        {/* ── Main content row: District Intel (center) + ROI (side) ─────── */}
        <div className="grid gap-5 lg:grid-cols-3">

          {/* ── District Intelligence Hub, MAIN ─────────────────────────── */}
          <div className="lg:col-span-2 rounded-2xl border border-white/[0.07] bg-[#0a1729]">
            <DistrictIntelligenceHub data={data} schools={schools} navigate={navigate}
              onSchoolClick={setSelectedSchool} />
          </div>

          {/* ── What This Means, collapsible side card ───────────────────── */}
          <RoiSideCard
            totalStudents={totalStudents}
            hoursSaved={hoursSaved}
            dollarValue={dollarValue}
            undecided={undecided}
          />
        </div>

        {/* ── School breakdown ──────────────────────────────────────────────── */}
        {schools.length > 0 && (
          <div>
            <h2 className="mb-4 text-sm font-semibold text-white">School Breakdown</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {schools.map((school) => {
                const eng = school.engagement || {};
                const rs = school.future_readiness_score ?? calcReadiness(eng);
                return (
                  <SchoolCard
                    key={school.id}
                    school={school}
                    engagement={eng}
                    readiness={rs}
                    onViewCounselors={() => navigate('/app/pathways/counselor')}
                    onDrillDown={() => setSelectedSchool(school)}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* No schools fallback (flat org) */}
        {schools.length === 0 && totalStudents > 0 && (
          <div className="rounded-xl border border-white/[0.07] bg-white/[0.015] p-6">
            <p className="text-sm text-slate-300">
              Your organisation is configured as a flat tenant (no child schools).
              All students are in the same pool. Use the Counselor Command Center to drill in.
            </p>
            <button
              onClick={() => navigate('/app/pathways/counselor')}
              className="mt-4 flex items-center gap-2 rounded-lg border border-teal-300/25
                bg-teal-400/[0.07] px-4 py-2 text-sm font-medium text-teal-300
                hover:bg-teal-400/[0.12] transition-colors">
              Open Counselor Command Center <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* School Pathway Modal */}
        {selectedSchool && (
          <SchoolPathwayModal school={selectedSchool} onClose={() => setSelectedSchool(null)} />
        )}

        {/* ── Bond Intelligence CTA ────────────────────────────────────────── */}
        <button
          onClick={() => navigate('/app/institution/bond-intelligence')}
          data-testid="bond-intelligence-btn"
          className="w-full rounded-2xl border p-6 text-left transition-all hover:brightness-105"
          style={{ borderColor: 'rgba(34,211,238,0.25)',
                   background: 'linear-gradient(135deg, rgba(3,13,23,0.95) 0%, rgba(13,31,60,0.7) 100%)' }}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl
                border border-cyan-400/30 bg-cyan-400/[0.08]">
                <DollarSign className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-cyan-300 mb-1">
                  Community Impact Intelligence
                </p>
                <h3 className="text-base font-bold text-white">
                  Wake County Bond Intelligence
                </h3>
                <p className="text-[12px] text-slate-300 mt-0.5 max-w-xl">
                  Live $832.3M capital plan · 14 projects · From school bond to human-capital outcomes.
                  Scenario Lab, project drilldowns, and boardroom presentation mode.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10
                px-2.5 py-1 text-[10.5px] font-semibold text-emerald-400">
                Live Public Data
              </span>
              <ArrowRight className="h-5 w-5 text-cyan-400" />
            </div>
          </div>
        </button>
      </div>
    </AppLayout>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function ScoreLabel({ label, desc, color, active }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-2 rounded-full" style={{ background: color, opacity: active ? 1 : 0.3 }} />
      <p className={`text-[10.5px] ${active ? 'text-slate-200' : 'text-slate-300'}`}>
        <span className="font-semibold">{label}</span>, {desc}
      </p>
    </div>
  );
}

function MetricTile({ icon: Icon, value, label, color, urgent, testId }) {
  return (
    <div className="rounded-xl border bg-[#0b1b30] p-5"
      style={{ borderColor: urgent ? 'rgba(248,113,113,0.35)' : `${color}30`,
               background: urgent ? 'rgba(248,113,113,0.05)' : 'rgba(4,15,23,0.9)' }}
      data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <Icon className="h-4 w-4" style={{ color: urgent ? '#f87171' : color }} />
      </div>
      <p className="text-3xl font-bold" style={{ color: urgent ? '#f87171' : color }}>{value}</p>
      <p className="mt-1 text-[11px] text-slate-300">{label}</p>
    </div>
  );
}

function RoiSideCard({ totalStudents, hoursSaved, dollarValue, undecided }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-2xl border p-5 space-y-4 self-start"
      style={{ borderColor: 'rgba(251,191,36,0.30)', background: 'rgba(251,191,36,0.04)' }}
      data-testid="roi-card">

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-amber-300" />
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-300">
            What This Means
          </p>
        </div>
        <button
          onClick={() => setExpanded(v => !v)}
          className="rounded-lg border border-white/10 bg-white/[0.03] p-1.5
            text-slate-300 hover:text-white hover:bg-white/[0.07] transition-colors"
          aria-label={expanded ? 'Collapse' : 'Expand'}>
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Three compact stat rows, always visible */}
      <div className="space-y-2.5">
        <div className="rounded-xl border border-amber-400/15 bg-amber-400/[0.04] p-3.5">
          <p className="text-2xl font-black text-amber-200 tabular-nums">{hoursSaved.toLocaleString()}<span className="text-base font-semibold">h</span></p>
          <p className="text-[11.5px] font-medium text-slate-300 mt-0.5">counselor hours freed annually</p>
          <p className="text-[10px] text-slate-300 mt-0.5 leading-relaxed">
            12 min × 3 meetings × {fmt(totalStudents)} students
          </p>
        </div>
        <div className="rounded-xl border border-amber-400/15 bg-amber-400/[0.04] p-3.5">
          <p className="text-2xl font-black text-amber-200 tabular-nums">${dollarValue.toLocaleString()}</p>
          <p className="text-[11.5px] font-medium text-slate-300 mt-0.5">recaptured counselor capacity</p>
          <p className="text-[10px] text-slate-300 mt-0.5">based on $75K avg. counselor salary</p>
        </div>
        <div className="rounded-xl border border-rose-400/20 bg-rose-400/[0.05] p-3.5">
          <p className="text-2xl font-black text-rose-300 tabular-nums">{fmt(undecided)}</p>
          <p className="text-[11.5px] font-medium text-slate-300 mt-0.5">undecided students</p>
          <p className="text-[10px] text-slate-300 mt-0.5">3× less likely to enroll in postsecondary</p>
        </div>
      </div>

      {/* Expanded detail paragraph */}
      {expanded && (
        <p className="text-[11.5px] text-slate-300 leading-relaxed border-t border-white/[0.06] pt-4">
          Every counseling meeting currently starts with 10-15 minutes of background gathering
          across 3 different systems. FuturePath gives counselors a pre-meeting brief before
          the student walks in. No prep time. No hunting for information. Just coaching.
        </p>
      )}
    </div>
  );
}


function SchoolCard({ school, engagement: eng, readiness, onViewCounselors, onDrillDown }) {
  const bars = [
    { label: 'Assessment', pct: eng.assessment_complete_pct, color: '#a78bfa' },
    { label: 'Direction',  pct: eng.direction_established_pct, color: '#34d399' },
    { label: 'Income Goal', pct: eng.income_goal_set_pct, color: '#22d3ee' },
    { label: 'Colleges',   pct: eng.college_planning_started_pct, color: '#fbbf24' },
  ];

  return (
    <div className="rounded-2xl border border-white/[0.08] bg-[#0b1b30] p-6"
      data-testid={`school-card-${school.id}`}>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-teal-300 shrink-0" />
            <p className="font-semibold text-white text-sm">{school.name || school.id}</p>
          </div>
          <p className="mt-1 text-[11px] text-slate-300">
            {fmt(school.total_students)} students · {school.undecided_count} undecided
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-2xl font-bold" style={{
            color: readiness >= 65 ? '#34d399' : readiness >= 40 ? '#fbbf24' : '#fb923c'
          }}>{readiness}</p>
          <p className="text-[10px] text-slate-300">readiness score</p>
        </div>
      </div>

      <div className="space-y-2.5">
        {bars.map(b => (
          <div key={b.label} className="flex items-center gap-3">
            <p className="w-20 shrink-0 text-[10.5px] text-slate-300">{b.label}</p>
            <div className="flex-1 h-1.5 rounded-full bg-white/[0.05]">
              <div className="h-1.5 rounded-full transition-all duration-700"
                style={{ width: `${b.pct ?? 0}%`, background: b.color, opacity: 0.85 }} />
            </div>
            <p className="w-8 shrink-0 text-right text-[10px] text-slate-300">{b.pct ?? 0}%</p>
          </div>
        ))}
      </div>

      <div className="mt-5 flex gap-2">
        <button
          onClick={onDrillDown}
          className="flex-1 flex items-center justify-center gap-2 rounded-xl border
            border-cyan-300/20 bg-cyan-400/[0.06] py-2.5 text-[11.5px] font-medium text-cyan-300
            hover:bg-cyan-400/[0.12] transition-colors"
          data-testid={`school-pathway-btn-${school.id}`}>
          Pathway Breakdown <BarChart3 className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onViewCounselors}
          className="flex-1 flex items-center justify-center gap-2 rounded-xl border
            border-teal-300/20 bg-teal-400/[0.06] py-2.5 text-[11.5px] font-medium text-teal-300
            hover:bg-teal-400/[0.12] transition-colors">
          Counselors <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}


// ── School Pathway Modal ────────────────────────────────────────────────────
function SchoolPathwayModal({ school, onClose }) {
  const pathway = school.pathway_distribution || {};
  const careers = school.career_interests || [];
  const eng = school.engagement || {};
  const rs = school.future_readiness_score || 0;

  const pathwayRows = [
    { key: 'four_year',         label: '4-Year University',       color: '#22d3ee' },
    { key: 'community_college', label: 'Community College',        color: '#a78bfa' },
    { key: 'skilled_trades',    label: 'Skilled Trades',           color: '#fbbf24' },
    { key: 'direct_employment', label: 'Direct Employment',        color: '#fb923c' },
    { key: 'military',          label: 'Military',                 color: '#34d399' },
    { key: 'entrepreneurship',  label: 'Entrepreneurship',         color: '#f472b6' },
    { key: 'undecided',         label: 'Undecided',                color: '#C6D4E8' },
  ].filter(r => (pathway[r.key] || 0) > 0);

  const pathTotal = Object.values(pathway).reduce((a, b) => a + b, 0) || 1;
  const rsColor = rs >= 65 ? '#34d399' : rs >= 40 ? '#fbbf24' : '#fb923c';

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="school-pathway-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0a1729]/70 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-2xl rounded-2xl border border-cyan-400/20
        bg-[#050f1c] shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b
          border-white/[0.07] bg-[#050f1c] px-6 py-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <GraduationCap className="h-4 w-4 text-cyan-400 shrink-0" />
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-cyan-300">
                School Pathway Breakdown
              </span>
            </div>
            <h2 className="text-lg font-bold text-white">{school.name || school.id}</h2>
            <p className="text-[11.5px] text-slate-300 mt-0.5">
              {fmt(school.total_students)} students · {school.undecided_count} undecided
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="text-right">
              <p className="text-3xl font-black tabular-nums" style={{ color: rsColor }}>{rs}</p>
              <p className="text-[10px] text-slate-300">readiness</p>
            </div>
            <button onClick={onClose}
              className="rounded-lg border border-white/10 bg-white/[0.04] p-2
                text-slate-300 hover:text-white hover:bg-white/[0.08] transition-colors"
              data-testid="close-school-modal">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">

          {/* Pathway proportion bar */}
          {pathwayRows.length > 0 && (
            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-300 mb-3">
                Postsecondary Pathway Mix
              </p>
              {/* Stacked bar */}
              <div className="flex h-6 w-full rounded-xl overflow-hidden mb-4">
                {pathwayRows.map(r => {
                  const w = (pathway[r.key] / pathTotal) * 100;
                  return w > 0 ? (
                    <div key={r.key} style={{ width: `${w}%`, background: r.color }}
                      title={`${r.label}: ${pathway[r.key]} students`} />
                  ) : null;
                })}
              </div>
              {/* Row breakdown */}
              <div className="space-y-2.5">
                {pathwayRows.map(r => {
                  const count = pathway[r.key] || 0;
                  const pctNum = Math.round((count / pathTotal) * 100);
                  return (
                    <div key={r.key} className="flex items-center gap-3"
                      data-testid={`modal-pathway-${r.key}`}>
                      <div className="h-3 w-3 rounded-sm shrink-0" style={{ background: r.color }} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[12px] text-slate-200">{r.label}</p>
                          <p className="text-[11px] text-slate-300 shrink-0">
                            {fmt(count)} · <strong style={{ color: r.color }}>{pctNum}%</strong>
                          </p>
                        </div>
                        <div className="mt-1 h-1 w-full rounded-full bg-white/[0.05]">
                          <div className="h-1 rounded-full transition-all duration-700"
                            style={{ width: `${pctNum}%`, background: r.color, opacity: 0.65 }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Engagement snapshot */}
          <div>
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-300 mb-3">
              Engagement Snapshot
            </p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Assessment', pct: eng.assessment_complete_pct, color: '#a78bfa' },
                { label: 'Direction Set', pct: eng.direction_established_pct, color: '#34d399' },
                { label: 'College Planning', pct: eng.college_planning_started_pct, color: '#fbbf24' },
              ].map(m => (
                <div key={m.label}
                  className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 text-center">
                  <p className="text-xl font-bold" style={{ color: m.color }}>{m.pct ?? 0}%</p>
                  <p className="text-[10px] text-slate-300 mt-0.5">{m.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Top career interests */}
          {careers.length > 0 && (
            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-300 mb-3">
                Top Career Interests
              </p>
              <div className="space-y-2">
                {careers.map((c, i) => {
                  const hue = ['#5eead4','#22d3ee','#a78bfa','#f472b6','#fbbf24','#34d399'][i % 6];
                  const barPct = careers[0]?.count > 0 ? Math.round((c.count / careers[0].count) * 100) : 0;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-5 text-[10px] text-slate-300 shrink-0 text-right">{i + 1}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-[11.5px] text-slate-200">{c.name}</p>
                          <p className="text-[10.5px]" style={{ color: hue }}>{c.count}</p>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-white/[0.05]">
                          <div className="h-1.5 rounded-full"
                            style={{ width: `${barPct}%`, background: hue }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {pathwayRows.length === 0 && careers.length === 0 && (
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 text-center">
              <p className="text-sm text-slate-300">
                No pathway or career data available for this school yet.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ── District Intelligence Hub ───────────────────────────────────────────────
const INTEL_TABS = ['Career Interests', 'Pathway Mix', 'School Rankings'];

function DistrictIntelligenceHub({ data, schools, navigate, onSchoolClick }) {
  const [tab, setTab] = useState('Career Interests');
  const allCareers  = data?.career_interests || [];
  const emergingCareers = data?.emerging_careers || [];
  const pathway     = data?.pathway_distribution || {};
  const totalStudents = data?.summary?.total_students || 0;

  const pathwayTotal = Object.values(pathway).reduce((a, b) => a + b, 0);
  const pathwayRows = [
    { key: 'four_year',          label: '4-Year University',   color: '#22d3ee' },
    { key: 'community_college',  label: 'Community College',   color: '#a78bfa' },
    { key: 'skilled_trades',     label: 'Skilled Trades & Blue Collar', color: '#fbbf24' },
    { key: 'direct_employment',  label: 'Direct Employment',   color: '#fb923c' },
    { key: 'military',           label: 'Military',            color: '#34d399' },
    { key: 'entrepreneurship',   label: 'Entrepreneurship',    color: '#f472b6' },
    { key: 'undecided',          label: 'Undecided',           color: '#94a3b8' },
  ].filter(r => (pathway[r.key] || 0) > 0);

  const topSchools = [...schools]
    .sort((a, b) => (b.future_readiness_score || 0) - (a.future_readiness_score || 0))
    .slice(0, 10);

  return (
    <div data-testid="district-intelligence-hub">
      {/* Tab bar */}
      <div className="flex items-center gap-0 border-b border-white/[0.07]">
        <div className="flex items-center gap-1.5 px-5 py-3">
          <BarChart3 className="h-3.5 w-3.5 text-teal-400" />
          <span className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-300 mr-2">
            District Intelligence
          </span>
        </div>
        {INTEL_TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            data-testid={`intel-tab-${t.toLowerCase().replace(/ /g, '-')}`}
            className={`px-4 py-3 text-xs font-medium border-b-2 transition-colors ${
              tab === t
                ? 'border-teal-400 text-teal-300'
                : 'border-transparent text-slate-300 hover:text-slate-300'
            }`}>
            {t}
          </button>
        ))}
      </div>

      <div className="p-5">
        {/* ── Career Interests ─────────────────────────────── */}
        {tab === 'Career Interests' && (
          <div data-testid="intel-career-interests">
            {(() => {
              const careerTotal = allCareers.reduce((s, c) => s + c.count, 0);
              return (
                <>
                  <p className="text-[11px] text-slate-300 mb-4">
                    Top career interests among {fmt(careerTotal)} students with a goal set
                  </p>
                  <div className="space-y-2.5">
                    {allCareers.map((c, i) => {
                      const pctNum = careerTotal > 0 ? Math.round((c.count / careerTotal) * 100 * 10) / 10 : 0;
                      const barPct = Math.round((c.count / (allCareers[0]?.count || 1)) * 100);
                      const hue = ['#5eead4','#22d3ee','#a78bfa','#f472b6','#fbbf24','#34d399','#60a5fa','#fb923c','#818cf8','#4ade80'][i % 10];
                      return (
                        <div key={i} className="flex items-center gap-3" data-testid={`career-row-${i}`}>
                          <span className="w-5 text-[10px] text-slate-300 shrink-0 text-right">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-[11.5px] text-slate-200 truncate pr-2">{c.name}</p>
                              <p className="text-[10.5px] text-slate-300 shrink-0">{fmt(c.count)} · <strong style={{ color: hue }}>{pctNum}%</strong></p>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-white/[0.05]">
                              <div className="h-1.5 rounded-full transition-all"
                                style={{ width: `${barPct}%`, background: hue }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* ── Emerging / Non-Traditional Spotlight ── */}
                  {emergingCareers.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-white/[0.06]">
                      <p className="text-[10.5px] font-semibold uppercase tracking-wider text-pink-400/80 mb-3">
                        Sports, Media & Entrepreneurship
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {emergingCareers.map((c, i) => (
                          <span key={i} className="flex items-center gap-1.5 rounded-full border border-pink-500/20
                            bg-pink-500/[0.06] px-3 py-1 text-[11px] text-pink-200">
                            {c.name}
                            <span className="text-pink-400/60 text-[10px]">{c.count}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* ── Pathway Mix ──────────────────────────────────── */}
        {tab === 'Pathway Mix' && (
          <div data-testid="intel-pathway-mix">
            <p className="text-[11px] text-slate-300 mb-4">
              Postsecondary direction mix across {fmt(pathwayTotal)} students with a direction set
            </p>

            {/* Visual proportion bar */}
            <div className="flex h-8 w-full rounded-xl overflow-hidden mb-5">
              {pathwayRows.map(r => {
                const w = pathwayTotal > 0 ? (pathway[r.key] / pathwayTotal) * 100 : 0;
                return w > 0 ? (
                  <div key={r.key} style={{ width: `${w}%`, background: r.color }}
                    title={`${r.label}: ${pathway[r.key]}`} />
                ) : null;
              })}
            </div>

            {/* Pathway breakdown rows */}
            <div className="space-y-3">
              {pathwayRows.map(r => {
                const count = pathway[r.key] || 0;
                const pctNum = pathwayTotal > 0 ? Math.round((count / pathwayTotal) * 100) : 0;
                return (
                  <div key={r.key} className="flex items-center gap-3" data-testid={`pathway-row-${r.key}`}>
                    <div className="h-3 w-3 rounded-sm shrink-0" style={{ background: r.color }} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-[12px] text-slate-200">{r.label}</p>
                        <p className="text-[11px] text-slate-300">{fmt(count)} students · <strong style={{ color: r.color }}>{pctNum}%</strong></p>
                      </div>
                      <div className="mt-1 h-1 w-full rounded-full bg-white/[0.05]">
                        <div className="h-1 rounded-full" style={{ width: `${pctNum}%`, background: r.color, opacity: 0.6 }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {pathway.undecided > 0 && (
              <div className="mt-4 p-3 rounded-lg bg-amber-500/8 border border-amber-500/20">
                <p className="text-[11px] text-amber-300">
                  <strong>{pathway.undecided}</strong> students ({Math.round((pathway.undecided / pathwayTotal) * 100)}%) haven't selected a postsecondary direction yet.
                  These are the top prospects for counselor outreach.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── School Rankings ───────────────────────────────── */}
        {tab === 'School Rankings' && (
          <div data-testid="intel-school-rankings">
            <p className="text-[11px] text-slate-300 mb-4">
              Top {topSchools.length} schools by Future Readiness Score, assessment + direction + income + college planning
            </p>
            <div className="space-y-2.5">
              {topSchools.map((s, i) => {
                const rs = s.future_readiness_score || 0;
                const eng = s.engagement || {};
                const rsColor = rs >= 75 ? '#34d399' : rs >= 55 ? '#fbbf24' : '#fb923c';
                return (
                  <button key={s.id}
                    onClick={() => onSchoolClick && onSchoolClick(s)}
                    className="w-full rounded-xl border border-white/[0.06] bg-white/[0.015] p-3
                      hover:border-cyan-400/25 hover:bg-white/[0.03] transition-all text-left"
                    data-testid={`school-rank-${i}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="w-6 text-[11px] font-bold text-slate-300 shrink-0">#{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[12px] font-semibold text-white truncate">{s.name}</p>
                          <div className="flex items-center gap-2 shrink-0">
                            <p className="text-lg font-bold" style={{ color: rsColor }}>{rs}</p>
                            <ChevronRight className="h-3 w-3 text-slate-300" />
                          </div>
                        </div>
                        <p className="text-[10px] text-slate-300">{fmt(s.total_students)} students · {s.undecided_count} undecided</p>
                      </div>
                    </div>
                    <div className="flex gap-3 pl-9">
                      {[
                        { label: 'Assessed', pct: eng.assessment_complete_pct, color: '#a78bfa' },
                        { label: 'Direction', pct: eng.direction_established_pct, color: '#34d399' },
                        { label: 'Colleges', pct: eng.college_planning_started_pct, color: '#fbbf24' },
                      ].map(b => (
                        <div key={b.label} className="flex-1 text-center">
                          <p className="text-[11px] font-semibold" style={{ color: b.color }}>{b.pct ?? 0}%</p>
                          <p className="text-[9px] text-slate-300">{b.label}</p>
                        </div>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
            <button onClick={() => navigate('/app/pathways/counselor')}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-teal-300/20
                bg-teal-400/[0.06] py-2.5 text-[11.5px] font-medium text-teal-300 hover:bg-teal-400/[0.12] transition-colors">
              Open Counselor Command Center <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
